import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Badge, Check, Close, VerifiedUser } from '@mui/icons-material';

import { IKiAusweisKarte, IKiAusweiseProposal } from '../Interfaces/IKi';
import { useUebernehmeAusweise } from '../hooks/reactQuerys/KiQueries';

/**
 * Die Ausweise eines Sammelfotos den Beteiligten zuordnen (#1336).
 *
 * Die Karte zeigt **jeden Ausschnitt als Bild**. Das ist ihr eigentlicher
 * Zweck: der Benutzer hat die Ausweise vor sich auf dem Tisch liegen und
 * vergleicht. Eine Liste von Namen wäre schneller gebaut und nutzlos — ob die
 * richtige Karte am richtigen Mandanten landet, sieht man nur am Bild.
 *
 * Was sich nicht sicher zuordnen ließ, steht oben und nicht unten. Bei
 * Eheleuten mit gleichem Nachnamen ist das der Normalfall, nicht die Ausnahme,
 * und eine Ausweiskopie am falschen Mandanten fällt niemandem mehr auf — sie
 * sieht überall gleich richtig aus.
 *
 * Abgelegt wird erst mit dem Klick, am Mandanten, als neue Version des
 * passenden Dokumenttyps.
 */

interface IProps {
  proposal: IKiAusweiseProposal;
}

const SEITE_LABEL: Record<IKiAusweisKarte['Seite'], string> = {
  vorderseite: 'Vorderseite',
  rueckseite: 'Rückseite',
  unklar: 'Seite unklar',
};

/** Eine Karte samt ihrer aktuellen Zuordnung. */
interface IZeile {
  karte: IKiAusweisKarte;
  /** `null` = nicht übernehmen. */
  idLegalClient: number | null;
}

const personText = (karte: IKiAusweisKarte): string => {
  const name = [karte.Vorname, karte.Nachname].filter(Boolean).join(' ');
  const geboren =
    karte.Geburtsdatum !== null ? `*${karte.Geburtsdatum.slice(0, 10)}` : '';

  return [name, geboren].filter(Boolean).join(', ') || 'nicht gelesen';
};

const AusweisZeile: React.FC<{
  zeile: IZeile;
  beteiligte: { idLegalClient: number; Name: string }[];
  onChange: (_idLegalClient: number | null) => void;
}> = ({ zeile, beteiligte, onChange }) => {
  const { karte } = zeile;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ py: 1 }}>
      {karte.Bild !== null ? (
        <Box
          component='img'
          src={`data:image/png;base64,${karte.Bild}`}
          alt={`Ausweis ${karte.Nummer}`}
          sx={{
            width: 160,
            flexShrink: 0,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            objectFit: 'contain',
          }}
        />
      ) : (
        <Box
          sx={{
            width: 160,
            height: 100,
            flexShrink: 0,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'warning.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
          }}
        >
          <Typography variant='caption' color='warning.main' align='center'>
            Kein Ausschnitt — wird nicht abgelegt
          </Typography>
        </Box>
      )}

      <Stack spacing={0.5} sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack
          direction='row'
          spacing={0.5}
          alignItems='center'
          flexWrap='wrap'
          useFlexGap
        >
          <Chip
            size='small'
            variant='outlined'
            label={SEITE_LABEL[karte.Seite]}
          />
          {karte.AusMrz && (
            <Tooltip title='Aus der maschinenlesbaren Zone, mit Prüfziffer nachgerechnet'>
              <Chip
                size='small'
                color='success'
                variant='outlined'
                icon={<VerifiedUser fontSize='small' />}
                label='geprüft'
              />
            </Tooltip>
          )}
          {karte.AusGegenseite === true && (
            <Tooltip title='Über die Ausweisnummer von der anderen Seite übernommen — diese Seite wurde nicht selbst gelesen'>
              <Chip
                size='small'
                variant='outlined'
                label='von der Gegenseite'
              />
            </Tooltip>
          )}
        </Stack>

        <Typography variant='body2'>{personText(karte)}</Typography>

        {karte.Ausweisnummer !== null && (
          <Typography variant='caption' color='text.secondary'>
            Nr. {karte.Ausweisnummer}
            {karte.NummerGeprueft ? '' : ' (Prüfziffer nicht bestätigt)'}
          </Typography>
        )}

        <TextField
          select
          size='small'
          sx={{ maxWidth: 360, mt: 0.5 }}
          value={
            zeile.idLegalClient === null ? 'keine' : String(zeile.idLegalClient)
          }
          onChange={event =>
            onChange(
              event.target.value === 'keine' ? null : Number(event.target.value)
            )
          }
        >
          <MenuItem value='keine'>Nicht übernehmen</MenuItem>
          {beteiligte.map(person => (
            <MenuItem
              key={`ki-ausweis-person-${person.idLegalClient}`}
              value={String(person.idLegalClient)}
            >
              {person.Name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </Stack>
  );
};

export const KiAusweiseProposalCard: React.FC<IProps> = ({ proposal }) => {
  const { uebernehmeAusweise, isPendingUebernehmeAusweise } =
    useUebernehmeAusweise();

  // Flach halten: eine Zeile je Ausweis, mit ihrer Zuordnung. Gruppiert wird
  // erst beim Absenden — sonst müsste jedes Umhängen zwei Listen umbauen.
  const [zeilen, setZeilen] = useState<IZeile[]>(() => [
    ...proposal.OhneZuordnungArray.map(karte => ({
      karte,
      idLegalClient: null as number | null,
    })),
    ...proposal.ZuordnungArray.flatMap(zuordnung =>
      zuordnung.KartenArray.map(karte => ({
        karte,
        idLegalClient: zuordnung.idLegalClient as number | null,
      }))
    ),
  ]);

  const [state, setState] = useState<'open' | 'done' | 'dismissed'>('open');
  const [error, setError] = useState<string | null>(null);
  const [angelegt, setAngelegt] = useState<{ Name: string; Seiten: number }[]>(
    []
  );
  const [uebersprungen, setUebersprungen] = useState<string[]>([]);

  const offen = useMemo(
    () => zeilen.filter(z => z.idLegalClient === null).length,
    [zeilen]
  );

  if (state === 'dismissed') {
    return null;
  }

  async function handleUebernehmen() {
    setError(null);

    // Nach Person gruppieren — so erwartet es der Endpunkt, und so entsteht
    // je Person eine Version mit ihren Seiten darin.
    const jePerson = new Map<number, Record<string, unknown>>();

    zeilen.forEach(zeile => {
      if (zeile.idLegalClient === null || zeile.karte.Bild === null) {
        return;
      }

      const person = proposal.BeteiligteArray.find(
        p => p.idLegalClient === zeile.idLegalClient
      );
      const eintrag = jePerson.get(zeile.idLegalClient) ?? {
        idLegalClient: zeile.idLegalClient,
        Name: person?.Name ?? 'Beteiligter',
        KartenArray: [] as IKiAusweisKarte[],
      };

      (eintrag.KartenArray as IKiAusweisKarte[]).push(zeile.karte);
      jePerson.set(zeile.idLegalClient, eintrag);
    });

    try {
      const result = await uebernehmeAusweise({
        ZuordnungArray: Array.from(jePerson.values()),
      });

      setAngelegt(result.AngelegtArray ?? []);
      setUebersprungen(result.Uebersprungen ?? []);
      setState('done');
    } catch (exception) {
      const detail =
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : '';

      setError(
        detail !== '' ? detail : 'Die Ausweise konnten nicht abgelegt werden.'
      );
    }
  }

  if (state === 'done') {
    return (
      <Stack spacing={1} sx={{ mt: 1 }}>
        <Alert severity='success'>
          {angelegt.length === 0
            ? 'Es wurde kein Ausweis abgelegt.'
            : angelegt
                .map(
                  e =>
                    `${e.Name} (${e.Seiten} Seite${e.Seiten === 1 ? '' : 'n'})`
                )
                .join(', ')}
        </Alert>
        {uebersprungen.length > 0 && (
          <Alert severity='warning' sx={{ py: 0 }}>
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {uebersprungen.map(zeile => (
                <Typography component='li' variant='caption' key={zeile}>
                  {zeile}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    );
  }

  return (
    <Paper
      variant='outlined'
      sx={{ p: 1.5, mt: 1, borderColor: 'primary.light' }}
    >
      <Stack spacing={1.5}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Badge fontSize='small' color='primary' />
          <Typography variant='subtitle2' sx={{ flexGrow: 1 }}>
            Ausweise zuordnen
          </Typography>
          <Chip
            size='small'
            variant='outlined'
            label={`${zeilen.length} erkannt`}
          />
        </Stack>

        {offen > 0 && (
          <Alert severity='warning' sx={{ py: 0 }}>
            {offen === 1
              ? 'Ein Ausweis ließ sich keiner Person sicher zuordnen.'
              : `${offen} Ausweise ließen sich keiner Person sicher zuordnen.`}{' '}
            Bitte unten auswählen oder auf „Nicht übernehmen" stehen lassen.
          </Alert>
        )}

        <Stack divider={<Divider flexItem />}>
          {zeilen.map((zeile, index) => (
            <AusweisZeile
              key={`ki-ausweis-${zeile.karte.Nummer}`}
              zeile={zeile}
              beteiligte={proposal.BeteiligteArray}
              onChange={idLegalClient =>
                setZeilen(current =>
                  current.map((eintrag, i) =>
                    i === index ? { ...eintrag, idLegalClient } : eintrag
                  )
                )
              }
            />
          ))}
        </Stack>

        {proposal.Hinweise.length > 0 && (
          <Alert severity='info' sx={{ py: 0 }}>
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {proposal.Hinweise.map(hinweis => (
                <Typography component='li' variant='caption' key={hinweis}>
                  {hinweis}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {proposal.Display.Reason !== null &&
          proposal.Display.Reason !== undefined && (
            <Typography variant='caption' color='text.secondary'>
              {proposal.Display.Reason}
            </Typography>
          )}

        {error !== null && <Alert severity='error'>{error}</Alert>}

        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            size='small'
            color='inherit'
            startIcon={<Close />}
            disabled={isPendingUebernehmeAusweise}
            onClick={() => setState('dismissed')}
          >
            Verwerfen
          </Button>
          <Button
            size='small'
            variant='contained'
            startIcon={
              isPendingUebernehmeAusweise ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <Check />
              )
            }
            disabled={
              isPendingUebernehmeAusweise ||
              zeilen.every(
                z => z.idLegalClient === null || z.karte.Bild === null
              )
            }
            onClick={() => void handleUebernehmen()}
          >
            Übernehmen
          </Button>
        </Stack>

        <Typography variant='caption' color='text.secondary'>
          Es wurde noch nichts abgelegt. Bitte die Bilder mit den Ausweisen
          vergleichen, die Ihnen vorliegen.
        </Typography>
      </Stack>
    </Paper>
  );
};
